import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { Plus, Trash2, Video, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { format, addMinutes } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Instructor, CourseMode } from '@/types';
import ClassWizardLayout from './ClassWizardLayout';
import { useClassWizard } from './ClassWizardContext';
import { AddSessionDialog } from './AddSessionDialog';

interface ClassSession {
  mode: CourseMode;
  start_date: string;
  end_date: string;
  duration_minutes: number;
  instructor_id: string;
  location_name?: string;
  location_address?: string;
}

const Step5Sessions = () => {
  const navigate = useNavigate();
  const { state, setState } = useClassWizard();
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  useEffect(() => {
    if (!state.selectedCourse) {
      toast.error('Selecione um curso primeiro');
      navigate('/class-wizard/step5');
      return;
    }
    fetchInstructors();
  }, [state.selectedCourse, navigate]);

  const fetchInstructors = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('instructors')
        .select('*');

      if (error) throw error;
      setInstructors(data || []);
    } catch (error: any) {
      toast.error('Erro ao carregar instrutores', {
        description: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddSession = ({
    mode,
    startDate,
    startTime,
    durationMinutes,
    instructorId,
    locationName,
    locationAddress
  }: {
    mode: CourseMode;
    startDate: Date;
    startTime: string;
    durationMinutes: number;
    instructorId: string;
    locationName?: string;
    locationAddress?: string;
  }) => {
    // Criar data e hora de início
    const [hours, minutes] = startTime.split(':').map(Number);
    const start = new Date(startDate);
    start.setHours(hours, minutes, 0, 0);

    // Calcular data e hora de término
    const end = addMinutes(start, durationMinutes);

    const newSession: ClassSession = {
      mode,
      start_date: start.toISOString(),
      end_date: end.toISOString(),
      duration_minutes: durationMinutes,
      instructor_id: instructorId,
      ...(mode === 'in-person' && {
        location_name: locationName,
        location_address: locationAddress
      })
    };

    setState(prev => ({ ...prev, sessions: [...prev.sessions, newSession] }));
    setIsAddDialogOpen(false);
    toast.success('Sessão adicionada com sucesso');
  };

  const handleRemoveSession = (index: number) => {
    setState(prev => ({ ...prev, sessions: prev.sessions.filter((_, i) => i !== index) }));
    toast.success('Sessão removida com sucesso');
  };

  const handleNext = () => {
    if (state.sessions.length === 0) {
      toast.error('Adicione pelo menos uma sessão para continuar');
      return;
    }
    navigate('/class-wizard/step7');
  };

  const getModeBadge = (mode: CourseMode) => {
    switch (mode) {
      case 'onlineSync':
        return (
          <Badge variant="outline" className="bg-green-100 text-green-800">
            <Video className="h-3 w-3 mr-1" />
            Online Síncrono
          </Badge>
        );
      case 'in-person':
        return (
          <Badge variant="outline" className="bg-purple-100 text-purple-800">
            <MapPin className="h-3 w-3 mr-1" />
            Presencial
          </Badge>
        );
      default:
        return <Badge variant="outline">Desconhecido</Badge>;
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
  };

  const getInstructorName = (instructorId: string) => {
    const instructor = instructors.find(i => i.id === instructorId);
    return instructor ? `${instructor.first_name} ${instructor.last_name}` : 'Instrutor não encontrado';
  };

  // Determinar a modalidade padrão com base no curso selecionado
  const getDefaultMode = (): CourseMode => {
    if (!state.selectedCourse) return 'onlineSync';
    
    if (Array.isArray(state.selectedCourse.mode)) {
      if (state.selectedCourse.mode.includes('onlineSync')) return 'onlineSync';
      if (state.selectedCourse.mode.includes('in-person')) return 'in-person';
    } else if (state.selectedCourse.mode === 'onlineSync' || state.selectedCourse.mode === 'in-person') {
      return state.selectedCourse.mode as CourseMode;
    }
    
    return 'onlineSync';
  };

  // Determinar a duração padrão com base no curso e modalidade
  const getDefaultDuration = (mode: CourseMode): number => {
    if (!state.selectedCourse) return 60;
    
    if (mode === 'onlineSync' && state.selectedCourse.duration_onlinesync) {
      return state.selectedCourse.duration_onlinesync;
    }
    if (mode === 'in-person' && state.selectedCourse.duration_inperson) {
      return state.selectedCourse.duration_inperson;
    }
    return state.selectedCourse.duration_minutes || 60;
  };

  return (
    <ClassWizardLayout
      currentStep={6}
      title="Adicionar Sessões"
      description={`Adicione sessões para o curso "${state.selectedCourse?.name}"`}
      onNext={handleNext}
      nextDisabled={state.sessions.length === 0}
      nextLabel="Revisar"
    >
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-extrabold">Sessões ({state.sessions.length})</h3>
          <Button onClick={() => setIsAddDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Adicionar Sessão
          </Button>
        </div>

        {state.sessions.length === 0 ? (
          <div className="text-center py-8 border rounded-md bg-muted/50">
            <p className="text-muted-foreground">Nenhuma sessão adicionada</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => setIsAddDialogOpen(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Sessão
            </Button>
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Modalidade</TableHead>
                  <TableHead>Data de Início</TableHead>
                  <TableHead>Duração</TableHead>
                  <TableHead>Instrutor</TableHead>
                  <TableHead>Local</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {state.sessions.map((session, index) => (
                  <TableRow key={index}>
                    <TableCell>{getModeBadge(session.mode)}</TableCell>
                    <TableCell>{formatDateTime(session.start_date)}</TableCell>
                    <TableCell>{session.duration_minutes} minutos</TableCell>
                    <TableCell>{getInstructorName(session.instructor_id)}</TableCell>
                    <TableCell>
                      {session.mode === 'in-person' && session.location_name ? (
                        <div className="text-sm">
                          <div className="font-extrabold">{session.location_name}</div>
                          {session.location_address && (
                            <div className="text-muted-foreground text-xs">{session.location_address}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">Online</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveSession(index)}
                        className="text-destructive hover:text-destructive/90 hover:bg-destructive/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <AddSessionDialog
          open={isAddDialogOpen}
          onOpenChange={setIsAddDialogOpen}
          onSubmit={handleAddSession}
          courseName={state.selectedCourse?.name || ''}
          instructors={instructors}
          defaultMode={getDefaultMode()}
          defaultDurationMinutes={getDefaultDuration(getDefaultMode())}
        />
      </div>
    </ClassWizardLayout>
  );
};

export default Step5Sessions;